import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';

export default function Quotes({ api }) {
  const [quotes, setQuotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedTab, setSelectedTab] = useState('all');
  
  // Form states
  const [newQuote, setNewQuote] = useState({
    title: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_company: '',
    customer_address: '',
    description: '',
    deal_id: '',
    items: [],
    global_discount_percent: 0,
    shipping_cost: 0,
    payment_terms: '30 dias',
    delivery_terms: 'A combinar',
    warranty_terms: '12 meses',
    validity_days: 30
  });
  
  const [newItem, setNewItem] = useState({
    product_name: '',
    description: '',
    quantity: 1,
    unit_price: 0,
    cost_price: 0,
    discount_percent: 0,
    tax_percent: 0
  });
  
  const [newProduct, setNewProduct] = useState({
    name: '',
    code: '',
    description: '',
    category: '',
    unit_price: 0,
    cost_price: 0,
    tax_percent: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [quotesRes, productsRes, dealsRes, statsRes] = await Promise.all([
        api.get('/quotes'),
        api.get('/products'),
        api.get('/deals'),
        api.get('/quotes/stats')
      ]);

      setQuotes(quotesRes.data?.items || []);
      setProducts(productsRes.data?.items || []);
      setDeals(dealsRes.data?.items || []);
      setStats(statsRes.data || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuote = async () => {
    try {
      if (!newQuote.title || !newQuote.customer_name || !newQuote.customer_email) {
        toast.error('Preencha os campos obrigatórios');
        return;
      }

      await api.post('/quotes', newQuote);
      toast.success('Cotação criada com sucesso! 💰');
      
      setShowCreateModal(false);
      resetNewQuote();
      fetchData();
    } catch (error) {
      console.error('Error creating quote:', error);
      toast.error('Erro ao criar cotação');
    }
  };

  const handleCreateProduct = async () => {
    try {
      if (!newProduct.name || !newProduct.code) {
        toast.error('Preencha o nome e código do produto');
        return;
      }

      await api.post('/products', newProduct);
      toast.success('Produto criado com sucesso! 📦');
      
      setShowProductModal(false);
      setNewProduct({
        name: '',
        code: '',
        description: '',
        category: '',
        unit_price: 0,
        cost_price: 0,
        tax_percent: 0
      });
      
      // Refetch products
      const productsRes = await api.get('/products');
      setProducts(productsRes.data || []);
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error('Erro ao criar produto');
    }
  };

  const handleSendQuote = async (quoteId) => {
    try {
      await api.post(`/quotes/${quoteId}/send`);
      toast.success('Cotação enviada com sucesso! 📧');
      fetchData();
    } catch (error) {
      console.error('Error sending quote:', error);
      toast.error('Erro ao enviar cotação');
    }
  };

  const handleApproveQuote = async (quoteId, action, notes = '') => {
    try {
      await api.post(`/quotes/${quoteId}/approve`, { action, notes });
      toast.success(`Cotação ${action === 'approve' ? 'aprovada' : 'rejeitada'} com sucesso! ✅`);
      setShowApprovalModal(false);
      fetchData();
    } catch (error) {
      console.error('Error approving quote:', error);
      toast.error('Erro ao processar aprovação');
    }
  };

  const handleConvertToProposal = async (quoteId) => {
    try {
      const response = await api.post(`/quotes/${quoteId}/convert-to-proposal`);
      toast.success('Cotação convertida em proposta com sucesso! 📋');
      fetchData();
    } catch (error) {
      console.error('Error converting to proposal:', error);
      toast.error('Erro ao converter cotação');
    }
  };

  const addItemToQuote = () => {
    if (!newItem.product_name || newItem.unit_price === 0) {
      toast.error('Preencha o nome e preço do item');
      return;
    }

    const total = calculateItemTotal(newItem);
    const itemWithTotal = { ...newItem, total, id: Date.now().toString() };
    
    setNewQuote(prev => ({
      ...prev,
      items: [...prev.items, itemWithTotal]
    }));
    
    setNewItem({
      product_name: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      cost_price: 0,
      discount_percent: 0,
      tax_percent: 0
    });
  };

  const removeItemFromQuote = (index) => {
    setNewQuote(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.unit_price;
    const discount = subtotal * (item.discount_percent / 100);
    const taxable = subtotal - discount;
    const tax = taxable * (item.tax_percent / 100);
    return taxable + tax;
  };

  const calculateQuoteTotal = (quote) => {
    const subtotal = quote.items.reduce((acc, item) => acc + (item.quantity * item.unit_price), 0);
    const globalDiscount = subtotal * (quote.global_discount_percent / 100);
    const tax = quote.items.reduce((acc, item) => {
      const itemSubtotal = item.quantity * item.unit_price;
      const itemDiscount = itemSubtotal * (item.discount_percent / 100);
      const taxable = itemSubtotal - itemDiscount;
      return acc + (taxable * (item.tax_percent / 100));
    }, 0);
    
    return subtotal - globalDiscount + tax + quote.shipping_cost;
  };

  const resetNewQuote = () => {
    setNewQuote({
      title: '',
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      customer_company: '',
      customer_address: '',
      description: '',
      deal_id: '',
      items: [],
      global_discount_percent: 0,
      shipping_cost: 0,
      payment_terms: '30 dias',
      delivery_terms: 'A combinar',
      warranty_terms: '12 meses',
      validity_days: 30
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-slate-100 text-slate-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'converted': return 'bg-purple-100 text-purple-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'draft': return 'Rascunho';
      case 'sent': return 'Enviada';
      case 'approved': return 'Aprovada';
      case 'rejected': return 'Rejeitada';
      case 'converted': return 'Convertida';
      default: return 'Desconhecido';
    }
  };

  const filteredQuotes = (quotes || []).filter(quote => {
    switch (selectedTab) {
      case 'draft': return quote.status === 'draft';
      case 'sent': return quote.status === 'sent';
      case 'approved': return quote.approval_status === 'approved';
      case 'pending': return quote.requires_approval && quote.approval_status === 'pending';
      default: return true;
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Sistema de Cotações</h1>
          <p className="text-slate-600 mt-1">Gerencie cotações, produtos e aprovações</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button 
            onClick={() => setShowProductModal(true)}
            variant="outline"
            className="border-purple-300 text-purple-600 hover:bg-purple-50"
          >
            📦 Produtos
          </Button>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-orange-500 to-purple-600 hover:from-orange-600 hover:to-purple-700 text-white"
          >
            💰 Nova Cotação
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total_quotes || 0}</p>
              </div>
              <div className="h-10 w-10 bg-gradient-to-r from-blue-100 to-blue-200 rounded-full flex items-center justify-center">
                <span className="text-lg">💰</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Aprovadas</p>
                <p className="text-2xl font-bold text-green-600">{stats.approved || 0}</p>
              </div>
              <div className="h-10 w-10 bg-gradient-to-r from-green-100 to-green-200 rounded-full flex items-center justify-center">
                <span className="text-lg">✅</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Convertidas</p>
                <p className="text-2xl font-bold text-purple-600">{stats.converted || 0}</p>
              </div>
              <div className="h-10 w-10 bg-gradient-to-r from-purple-100 to-purple-200 rounded-full flex items-center justify-center">
                <span className="text-lg">📋</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Taxa Aprovação</p>
                <p className="text-2xl font-bold text-blue-600">{(stats.approval_rate || 0).toFixed(1)}%</p>
              </div>
              <div className="h-10 w-10 bg-gradient-to-r from-blue-100 to-blue-200 rounded-full flex items-center justify-center">
                <span className="text-lg">📈</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Valor Total</p>
                <p className="text-xl font-bold text-orange-600">
                  {new Intl.NumberFormat('pt-BR', { 
                    style: 'currency', 
                    currency: 'BRL',
                    minimumFractionDigits: 0
                  }).format(stats.total_value || 0)}
                </p>
              </div>
              <div className="h-10 w-10 bg-gradient-to-r from-orange-100 to-orange-200 rounded-full flex items-center justify-center">
                <span className="text-lg">💎</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="all">Todas</TabsTrigger>
          <TabsTrigger value="draft">Rascunhos</TabsTrigger>
          <TabsTrigger value="sent">Enviadas</TabsTrigger>
          <TabsTrigger value="approved">Aprovadas</TabsTrigger>
          <TabsTrigger value="pending">Pendentes</TabsTrigger>
        </TabsList>

        <TabsContent value={selectedTab} className="space-y-4 mt-6">
          {filteredQuotes.length === 0 ? (
            <Card className="glass border-white/20">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💰</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                  Nenhuma cotação encontrada
                </h3>
                <p className="text-slate-500 mb-4">
                  {selectedTab === 'all' 
                    ? 'Crie sua primeira cotação para começar'
                    : `Nenhuma cotação ${getStatusLabel(selectedTab).toLowerCase()} encontrada`
                  }
                </p>
                {selectedTab === 'all' && (
                  <Button 
                    onClick={() => setShowCreateModal(true)}
                    className="bg-gradient-to-r from-orange-500 to-purple-600 hover:from-orange-600 hover:to-purple-700 text-white"
                  >
                    💰 Criar Primeira Cotação
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredQuotes.map((quote) => (
                <Card key={quote.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-slate-900">
                            {quote.title}
                          </h3>
                          <Badge className={getStatusColor(quote.status)}>
                            {getStatusLabel(quote.status)}
                          </Badge>
                          {quote.requires_approval && (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              Precisa Aprovação
                            </Badge>
                          )}
                          <span className="text-sm text-slate-500">
                            {quote.quote_number}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-600">Cliente</p>
                            <p className="font-medium">{quote.customer_name}</p>
                            {quote.customer_company && (
                              <p className="text-slate-500">{quote.customer_company}</p>
                            )}
                          </div>
                          
                          <div>
                            <p className="text-slate-600">Valor</p>
                            <p className="font-bold text-green-600">
                              {new Intl.NumberFormat('pt-BR', { 
                                style: 'currency', 
                                currency: 'BRL' 
                              }).format(quote.total_amount)}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-slate-600">Itens</p>
                            <p className="font-medium">{quote.items?.length || 0} itens</p>
                          </div>
                          
                          <div>
                            <p className="text-slate-600">Válida até</p>
                            <p className="font-medium">
                              {quote.valid_until ? 
                                new Date(quote.valid_until).toLocaleDateString('pt-BR') : 
                                'Não definido'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-6">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setSelectedQuote(quote);
                            setShowDetailModal(true);
                          }}
                        >
                          👁️ Ver
                        </Button>
                        
                        {quote.status === 'draft' && (
                          <Button 
                            size="sm"
                            onClick={() => handleSendQuote(quote.id)}
                            className="bg-blue-100 text-blue-700 hover:bg-blue-200"
                          >
                            📧 Enviar
                          </Button>
                        )}
                        
                        {quote.requires_approval && quote.approval_status === 'pending' && (
                          <Button 
                            size="sm"
                            onClick={() => {
                              setSelectedQuote(quote);
                              setShowApprovalModal(true);
                            }}
                            className="bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                          >
                            ⚖️ Aprovar
                          </Button>
                        )}
                        
                        {quote.approval_status === 'approved' && quote.status !== 'converted' && (
                          <Button 
                            size="sm"
                            onClick={() => handleConvertToProposal(quote.id)}
                            className="bg-purple-100 text-purple-700 hover:bg-purple-200"
                          >
                            📋 Converter
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Quote Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <span>💰</span>
              <span>Nova Cotação</span>
            </DialogTitle>
            <DialogDescription>
              Crie uma nova cotação para enviar ao cliente
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-3 gap-6">
            {/* Left Column - Basic Info */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Título da Cotação *</Label>
                <Input
                  value={newQuote.title}
                  onChange={(e) => setNewQuote(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Ex: Cotação Sistema CRM"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Nome do Cliente *</Label>
                <Input
                  value={newQuote.customer_name}
                  onChange={(e) => setNewQuote(prev => ({ ...prev, customer_name: e.target.value }))}
                  placeholder="Nome completo"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email do Cliente *</Label>
                <Input
                  type="email"
                  value={newQuote.customer_email}
                  onChange={(e) => setNewQuote(prev => ({ ...prev, customer_email: e.target.value }))}
                  placeholder="email@exemplo.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  value={newQuote.customer_phone}
                  onChange={(e) => setNewQuote(prev => ({ ...prev, customer_phone: e.target.value }))}
                  placeholder="(11) 99999-9999"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Empresa</Label>
                <Input
                  value={newQuote.customer_company}
                  onChange={(e) => setNewQuote(prev => ({ ...prev, customer_company: e.target.value }))}
                  placeholder="Nome da empresa"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Relacionar com Negócio</Label>
                <Select 
                  value={newQuote.deal_id}
                  onValueChange={(value) => setNewQuote(prev => ({ ...prev, deal_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Opcional" />
                  </SelectTrigger>
                  <SelectContent>
                    {deals.map((deal) => (
                      <SelectItem key={deal.id} value={deal.id}>
                        {deal.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Middle Column - Items */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">Itens da Cotação</Label>
              
              {/* Add Item Form */}
              <Card className="p-4 bg-slate-50">
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Nome do produto/serviço"
                      value={newItem.product_name}
                      onChange={(e) => setNewItem(prev => ({ ...prev, product_name: e.target.value }))}
                    />
                    <Input
                      placeholder="Descrição"
                      value={newItem.description}
                      onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      type="number"
                      placeholder="Qtd"
                      value={newItem.quantity}
                      onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 1 }))}
                    />
                    <Input
                      type="number"
                      placeholder="Preço unitário"
                      value={newItem.unit_price}
                      onChange={(e) => setNewItem(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))}
                    />
                    <Input
                      type="number"
                      placeholder="% Desconto"
                      value={newItem.discount_percent}
                      onChange={(e) => setNewItem(prev => ({ ...prev, discount_percent: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                  
                  <Button 
                    size="sm"
                    onClick={addItemToQuote}
                    className="w-full"
                    disabled={!newItem.product_name || newItem.unit_price === 0}
                  >
                    ➕ Adicionar Item
                  </Button>
                </div>
              </Card>
              
              {/* Items List */}
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {newQuote.items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.product_name}</p>
                      <p className="text-xs text-slate-600">
                        {item.quantity}x R$ {item.unit_price.toFixed(2)} = R$ {calculateItemTotal(item).toFixed(2)}
                      </p>
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => removeItemFromQuote(index)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      🗑️
                    </Button>
                  </div>
                ))}
              </div>
              
              {newQuote.items.length > 0 && (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 p-3 rounded-lg border border-green-200">
                  <p className="font-semibold text-green-800">
                    Total: R$ {calculateQuoteTotal(newQuote).toFixed(2)}
                  </p>
                </div>
              )}
            </div>
            
            {/* Right Column - Terms */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea
                  value={newQuote.description}
                  onChange={(e) => setNewQuote(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descrição da cotação..."
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Desconto Global (%)</Label>
                <Input
                  type="number"
                  value={newQuote.global_discount_percent}
                  onChange={(e) => setNewQuote(prev => ({ ...prev, global_discount_percent: parseFloat(e.target.value) || 0 }))}
                  placeholder="0"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Frete</Label>
                <Input
                  type="number"
                  value={newQuote.shipping_cost}
                  onChange={(e) => setNewQuote(prev => ({ ...prev, shipping_cost: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Prazo de Pagamento</Label>
                <Select 
                  value={newQuote.payment_terms}
                  onValueChange={(value) => setNewQuote(prev => ({ ...prev, payment_terms: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="À vista">À vista</SelectItem>
                    <SelectItem value="15 dias">15 dias</SelectItem>
                    <SelectItem value="30 dias">30 dias</SelectItem>
                    <SelectItem value="45 dias">45 dias</SelectItem>
                    <SelectItem value="60 dias">60 dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Condições de Entrega</Label>
                <Select 
                  value={newQuote.delivery_terms}
                  onValueChange={(value) => setNewQuote(prev => ({ ...prev, delivery_terms: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CIF">CIF</SelectItem>
                    <SelectItem value="FOB">FOB</SelectItem>
                    <SelectItem value="A combinar">A combinar</SelectItem>
                    <SelectItem value="Retirada no local">Retirada no local</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Garantia</Label>
                <Input
                  value={newQuote.warranty_terms}
                  onChange={(e) => setNewQuote(prev => ({ ...prev, warranty_terms: e.target.value }))}
                  placeholder="12 meses"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Validade (dias)</Label>
                <Input
                  type="number"
                  value={newQuote.validity_days}
                  onChange={(e) => setNewQuote(prev => ({ ...prev, validity_days: parseInt(e.target.value) || 30 }))}
                  placeholder="30"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateQuote}
              className="bg-gradient-to-r from-orange-500 to-purple-600 hover:from-orange-600 hover:to-purple-700 text-white"
            >
              💰 Criar Cotação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <span>📦</span>
              <span>Novo Produto</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome do Produto *</Label>
              <Input
                value={newProduct.name}
                onChange={(e) => setNewProduct(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Licença CRM Premium"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Código *</Label>
              <Input
                value={newProduct.code}
                onChange={(e) => setNewProduct(prev => ({ ...prev, code: e.target.value }))}
                placeholder="Ex: CRM-PREM-001"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Textarea
                value={newProduct.description}
                onChange={(e) => setNewProduct(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descrição detalhada do produto..."
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Preço de Venda</Label>
                <Input
                  type="number"
                  value={newProduct.unit_price}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Preço de Custo</Label>
                <Input
                  type="number"
                  value={newProduct.cost_price}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, cost_price: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Input
                value={newProduct.category}
                onChange={(e) => setNewProduct(prev => ({ ...prev, category: e.target.value }))}
                placeholder="Ex: Software, Hardware, Serviços"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateProduct}>
              📦 Criar Produto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}